import { useQuery } from '@tanstack/react-query';
import { FormattedNumber } from 'react-intl';
import type { LocaleKey } from 'messages';
import { getApiCurrenciesRatesOptions } from '../../api/generated/@tanstack/react-query.gen';
import { useAppState } from '../app-state';
import { staletime } from '../tanstack-query-client';
import { CapitalizeFirstLetter } from './string-helpers';

const priceFormat = {
  style: 'currency',
  currencyDisplay: 'narrowSymbol',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
} as const;

interface FormattedPriceProps {
  amount: number;
}

export function FormattedPrice({ amount }: FormattedPriceProps) {
  const { currency } = useAppState();
  const currencyQuery = useQuery({
    ...getApiCurrenciesRatesOptions(),
    staleTime: staletime.MEDIUM,
  });
  const selectedCurrency = currencyQuery.data?.currencyRates?.find(
    (c: any) => c.currencyKey === currency
  );
  // Rate is home-units per target unit, so divide to convert.
  return (
    <FormattedNumber
      {...priceFormat}
      currency={currency}
      value={amount / (selectedCurrency?.value ?? 1)}
    />
  );
}

/**
 * A price of `digits` whole digits as `FormattedPrice` writes it in the
 * current currency, for keeping a column of prices that wide. Wide digits
 * stand in, so a real price of that length fits.
 */
export function PriceWidthPlaceholder({ digits }: { digits: number }) {
  const { currency } = useAppState();
  return (
    <FormattedNumber
      {...priceFormat}
      currency={currency}
      value={Number('8'.repeat(digits))}
    />
  );
}

export function currencyLongName(currencyKey: string, locale: LocaleKey) {
  try {
    const formattedCurrency = new Intl.NumberFormat(locale || 'en-GB', {
      style: 'currency',
      currency: currencyKey,
      currencyDisplay: 'name',
    }).format(0);

    const currencyName = formattedCurrency.replace(/[^a-z ]/gi, '').trim();
    return CapitalizeFirstLetter(currencyName);
  } catch {
    return currencyKey;
  }
}

export function currencySymbol(currencyKey: string, locale: LocaleKey) {
  try {
    const formattedCurrency = new Intl.NumberFormat(locale || 'en-GB', {
      style: 'currency',
      currency: currencyKey,
      currencyDisplay: 'narrowSymbol',
    }).format(0);

    const currencySymbol = formattedCurrency.replace(/[\d.,]/g, '').trim();
    return currencySymbol;
  } catch {
    return currencyKey;
  }
}

export function useCurrencyValue() {
  const { currency } = useAppState();
  const currencyQuery = useQuery({
    ...getApiCurrenciesRatesOptions(),
    staleTime: staletime.MEDIUM,
  });
  const selectedCurrency = currencyQuery.data?.currencyRates?.find(
    (c: any) => c.currencyKey === currency
  );

  return selectedCurrency?.value ?? 1;
}
